import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useNavigate } from "react-router-dom";
import Layout from "../Common/Layout";

interface ReviewProps {
  onNext?: (data?: any) => void;
  onPrevious?: () => void;
  workflowData?: any;
  currentStep?: number;
  totalSteps?: number;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  onNavigateToStep?: (stepIndex: number) => void;
  loading?: boolean;
}

interface ReviewData {
  // Contact Information
  contactInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    dateOfBirth: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
  };

  // Driver Information
  drivers: Array<{
    id: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    gender: string;
    maritalStatus: string;
    relationship?: string;
    driverType?: string;
    education: string;
    occupation: string;
    dlNumber: string;
    dlState: string;
    dlYear: string;
    accidentsOrClaims: string;
    residencyType: string;
    residencyStatus: string;
    priorInsurance: string;
    timeWithPriorInsurance: string;
    priorExpirationDate: string;
    priorInAgency: string;
    priorInsuranceCarrier: string;
    priorLiabilityLimits: string;
    priorTransferLevel: string;
    parentsPolicy: string;
  }>;

  // Vehicle Information
  vehicles: Array<{
    id: string;
    year: string;
    make: string;
    model: string;
    trim: string;
    vin: string;
    mileage: string;
    bodyType: string;
    color: string;
    usage: string;
    parkingLocation: string;
    compDed?: string;
    collDed?: string;
  }>;

  // Policy Coverage
  policyCoverage: {
    policyEffectiveDate: string;
    policyExpirationDate: string;
    term?: string;
    bodilyInjury?: string;
    propertyDamage?: string;
    pipType?: string;
    pipDed?: string;
    pipDedOption?: string;
    wageLossExclusion?: string;
    medicalPaymentLimit?: string;
    uninsuredType?: string;
    umbiLimit?: string;
    uimbiLimit?: string;
    uninsuredPdLimit?: string;
    uninsuredPdDeductible?: string;
    underinsuredPd?: string;
    umStackedMedicalPayment?: string;
    accidentalDeathLimit?: string;
  };
}

export default function Review({
  onNext,
  onPrevious,
  workflowData,
  currentStep,
  totalSteps,
  isFirstStep,
  isLastStep,
  onNavigateToStep,
  loading,
}: ReviewProps = {}) {
  const navigate = useNavigate();
  const [editingSection, setEditingSection] = useState<string | null>(null);
  useEffect(() => {
    // Scroll main layout-content container to top
    const layoutContent = document.querySelector(".layout-content");
    if (layoutContent) {
      layoutContent.scrollTo({ top: 0, behavior: "auto" });
    }
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);
  const handleClearForm = () => {
    // Clear form logic can be implemented here
  };

  const Content = () => {
    // Get data from workflowData instead of mock data
    const contactInfo = workflowData?.step1 || {};
    const drivers = workflowData?.step2 || [];
    const vehicles = workflowData?.step3 || [];
    const policyCoverage = workflowData?.step4 || {};

    const [reviewData, setReviewData] = useState<ReviewData>({
      contactInfo: {
        firstName: contactInfo.firstName || "",
        lastName: contactInfo.lastName || "",
        email: contactInfo.email || "",
        phone: contactInfo.cellphone || "",
        dateOfBirth: contactInfo.dateOfBirth || "",
        address: contactInfo.address || "",
        city: contactInfo.city || "",
        state: contactInfo.state || "",
        zipCode: contactInfo.zipCode || "",
      },
      drivers: Array.isArray(drivers)
        ? drivers.map((driver: any) => ({
            id: driver.id || "",
            firstName: driver.firstName || "",
            lastName: driver.lastName || "",
            dateOfBirth: driver.dateOfBirth || "",
            gender: driver.gender || "",
            maritalStatus: driver.maritalStatus || "",
            relationship: driver.relationship || "",
            driverType: driver.driverType || "",
            education: driver.education || "",
            occupation: driver.occupation || "",
            dlNumber: driver.dlNumber || "",
            dlState: driver.dlState || "",
            dlYear: driver.dlYear || "",
            accidentsOrClaims: driver.accidentsOrClaims || "",
            residencyType: driver.residencyType || "",
            residencyStatus: driver.residencyStatus || "",
            priorInsurance: driver.priorInsurance || "",
            timeWithPriorInsurance: driver.timeWithPriorInsurance || "",
            priorExpirationDate: driver.priorExpirationDate || "",
            priorInAgency: driver.priorInAgency || "",
            priorInsuranceCarrier: driver.priorInsuranceCarrier || "",
            priorLiabilityLimits: driver.priorLiabilityLimits || "",
            priorTransferLevel: driver.priorTransferLevel || "",
            parentsPolicy: driver.parentsPolicy || "",
          }))
        : [],
      vehicles: Array.isArray(vehicles)
        ? vehicles.map((vehicle: any) => ({
            id: vehicle.id || "",
            year: vehicle.year || "",
            make: vehicle.make || "",
            model: vehicle.model || "",
            trim: vehicle.trim || "",
            vin: vehicle.vin || "",
            mileage: vehicle.mileage || "",
            bodyType: vehicle.bodyType || "",
            color: vehicle.color || "",
            usage: vehicle.usage || "",
            parkingLocation: vehicle.parkingLocation || "",
            compDed: vehicle.comprehensiveDeductible || "",
            collDed: vehicle.collisionDeductible || "",
          }))
        : [],
      policyCoverage: {
        policyEffectiveDate: policyCoverage.policyEffectiveDate || "",
        policyExpirationDate: policyCoverage.policyExpirationDate || "",
        term: policyCoverage.term || "",
        bodilyInjury: policyCoverage.bodilyInjury || policyCoverage.bodilyInjuryLimit || "",
        propertyDamage: policyCoverage.propertyDamage || policyCoverage.propertyDamageLimit || "",
        pipType: policyCoverage.pipType || "",
        pipDed: policyCoverage.pipDed || "",
        pipDedOption: policyCoverage.pipDedOption || "",
        wageLossExclusion: policyCoverage.wageLossExclusion || "",
        medicalPaymentLimit: policyCoverage.medicalPaymentLimit || policyCoverage.medicalPaymentLimit || "",
        uninsuredType: policyCoverage.uninsuredType || "",
        umbiLimit: policyCoverage.umbiLimit || "",
        uimbiLimit: policyCoverage.uimbiLimit || "",
        uninsuredPdLimit: policyCoverage.uninsuredPdLimit || "",
        uninsuredPdDeductible: policyCoverage.uninsuredPdDeductible || "",
        underinsuredPd: policyCoverage.underinsuredPd || "",
        umStackedMedicalPayment: policyCoverage.umStackedMedicalPayment || "",
        accidentalDeathLimit: policyCoverage.accidentalDeathLimit || "",
      },
    });

    // Update reviewData when workflowData changes
    useEffect(() => {
      if (workflowData) {
        const contactInfo = workflowData.step1 || {};
        const drivers = workflowData.step2 || [];
        const vehicles = workflowData.step3 || [];
        const policyCoverage = workflowData.step4 || {};

        setReviewData({
          contactInfo: {
            firstName: contactInfo.firstName || "",
            lastName: contactInfo.lastName || "",
            email: contactInfo.email || "",
            phone: contactInfo.cellphone || "",
            dateOfBirth: contactInfo.dateOfBirth || "",
            address: contactInfo.address || "",
            city: contactInfo.city || "",
            state: contactInfo.state || "",
            zipCode: contactInfo.zipCode || "",
          },
          drivers: Array.isArray(drivers)
            ? drivers.map((driver: any) => ({
                id: driver.id || "",
                firstName: driver.firstName || "",
                lastName: driver.lastName || "",
                dateOfBirth: driver.dateOfBirth || "",
                gender: driver.gender || "",
                maritalStatus: driver.maritalStatus || "",
                relationship: driver.relationship || "",
                driverType: driver.driverType || "",
                education: driver.education || "",
                occupation: driver.occupation || "",
                dlNumber: driver.dlNumber || "",
                dlState: driver.dlState || "",
                dlYear: driver.dlYear || "",
                accidentsOrClaims: driver.accidentsOrClaims || "",
                residencyType: driver.residencyType || "",
                residencyStatus: driver.residencyStatus || "",
                priorInsurance: driver.priorInsurance || "",
                timeWithPriorInsurance: driver.timeWithPriorInsurance || "",
                priorExpirationDate: driver.priorExpirationDate || "",
                priorInAgency: driver.priorInAgency || "",
                priorInsuranceCarrier: driver.priorInsuranceCarrier || "",
                priorLiabilityLimits: driver.priorLiabilityLimits || "",
                priorTransferLevel: driver.priorTransferLevel || "",
                parentsPolicy: driver.parentsPolicy || "",
              }))
            : [],
          vehicles: Array.isArray(vehicles)
            ? vehicles.map((vehicle: any) => ({
                id: vehicle.id || "",
                year: vehicle.year || "",
                make: vehicle.make || "",
                model: vehicle.model || "",
                trim: vehicle.trim || "",
                vin: vehicle.vin || "",
                mileage: vehicle.mileage || "",
                bodyType: vehicle.bodyType || "",
                color: vehicle.color || "",
                usage: vehicle.usage || "",
                parkingLocation: vehicle.parkingLocation || "",
                compDed: vehicle.comprehensiveDeductible || "",
                collDed: vehicle.collisionDeductible || "",
              }))
            : [],
          policyCoverage: {
            policyEffectiveDate: policyCoverage.policyEffectiveDate || "",
            policyExpirationDate: policyCoverage.policyExpirationDate || "",
            term: policyCoverage.term || "",
            bodilyInjury: policyCoverage.bodilyInjury || policyCoverage.bodilyInjuryLimit || "",
            propertyDamage: policyCoverage.propertyDamage || policyCoverage.propertyDamageLimit || "",
            pipType: policyCoverage.pipType || "",
            pipDed: policyCoverage.pipDed || "",
            pipDedOption: policyCoverage.pipDedOption || "",
            wageLossExclusion: policyCoverage.wageLossExclusion || "",
            medicalPaymentLimit: policyCoverage.medicalPaymentLimit || policyCoverage.medicalPaymentLimit || "",
            uninsuredType: policyCoverage.uninsuredType || "",
            umbiLimit: policyCoverage.umbiLimit || "",
            uimbiLimit: policyCoverage.uimbiLimit || "",
            uninsuredPdLimit: policyCoverage.uninsuredPdLimit || "",
            uninsuredPdDeductible: policyCoverage.uninsuredPdDeductible || "",
            underinsuredPd: policyCoverage.underinsuredPd || "",
            umStackedMedicalPayment: policyCoverage.umStackedMedicalPayment || "",
            accidentalDeathLimit: policyCoverage.accidentalDeathLimit || "",
          },
        });
      }
    }, [workflowData]);

    // Helper function to get state-specific coverage data
    const getCoverageTableData = (reviewData: ReviewData, selectedState: string) => {
      // Map of required coverages by state (same as in PolicyCoverage.tsx)
      const stateCoverageMap: Record<string, string[]> = {
        Alabama: ["bodilyInjury", "propertyDamage", "uninsuredBI", "medicalPayment", "accidentalDeath"],
        Florida: [
          "bodilyInjury",
          "propertyDamage",
          "pip",
          "pipDed",
          "pipDedOption",
          "wageLossExclusion",
          "uninsuredBI",
          "medicalPayment",
          "accidentalDeath",
          "umStackedMedicalPayment",
        ],
        Georgia: [
          "bodilyInjury",
          "propertyDamage",
          "medicalPayment",
          "accidentalDeath",
          "uninsuredType",
          "uninsuredBI",
          "uninsuredPD",
          "uninsuredPDDed",
        ],
        Ohio: ["bodilyInjury", "propertyDamage", "uninsuredBI", "underinsuredBI", "uninsuredPD", "medicalPayment", "accidentalDeath"],
        "South Carolina": [
          "bodilyInjury",
          "propertyDamage",
          "uninsuredBI",
          "underinsuredBI",
          "uninsuredPD",
          "underinsuredPD",
          "medicalPayment",
          "accidentalDeath",
        ],
        Tennessee: ["bodilyInjury", "propertyDamage", "uninsuredBI", "uninsuredPD", "medicalPayment", "accidentalDeath"],
      };

      const requiredCoverages = stateCoverageMap[selectedState] || [];
      const coverageData: { name: string; amount: string }[] = [];

      // Return empty if no state selected or no coverages for that state
      if (!selectedState || requiredCoverages.length === 0) {
        return [];
      }

      // Build coverage data array based on state requirements
      requiredCoverages.forEach((coverage) => {
        switch (coverage) {
          case "bodilyInjury":
            if (reviewData.policyCoverage.bodilyInjury) {
              coverageData.push({
                name: "Bodily Injury",
                amount: `${reviewData.policyCoverage.bodilyInjury}`,
              });
            }
            break;
             case "propertyDamage":
            if ( reviewData.policyCoverage.propertyDamage) {
              coverageData.push({
                name: "Property Damage",
                amount: `${reviewData.policyCoverage.propertyDamage}`,
              });
            }
            break;
          case "pip":
            if (reviewData.policyCoverage.pipType) {
              coverageData.push({
                name: "Personal Injury Protection (PIP)",
                amount: reviewData.policyCoverage.pipType,
              });
            }
            break;
          case "pipDed":
            if (reviewData.policyCoverage.pipDed) {
              coverageData.push({
                name: "PIP Deductible",
                amount: reviewData.policyCoverage.pipDed,
              });
            }
            break;
          case "pipDedOption":
            if (reviewData.policyCoverage.pipDedOption) {
              coverageData.push({
                name: "PIP Deductible Option",
                amount: reviewData.policyCoverage.pipDedOption,
              });
            }
            break;
          case "wageLossExclusion":
            if (reviewData.policyCoverage.wageLossExclusion) {
              coverageData.push({
                name: "Wage Loss Exclusion",
                amount: reviewData.policyCoverage.wageLossExclusion,
              });
            }
            break;
          case "uninsuredType":
            if (reviewData.policyCoverage.uninsuredType) {
              coverageData.push({
                name: "Uninsured Type",
                amount: reviewData.policyCoverage.uninsuredType,
              });
            }
            break;
          case "uninsuredBI":
            if (reviewData.policyCoverage.umbiLimit) {
              coverageData.push({
                name: "Uninsured Motorist BI (UMBI)",
                amount: reviewData.policyCoverage.umbiLimit,
              });
            }
            break;
          case "underinsuredBI":
            if (reviewData.policyCoverage.uimbiLimit) {
              coverageData.push({
                name: "Underinsured Motorist BI (UIMBI)",
                amount: reviewData.policyCoverage.uimbiLimit,
              });
            }
            break;
          case "uninsuredPD":
            if (reviewData.policyCoverage.uninsuredPdLimit) {
              coverageData.push({
                name: "Uninsured Motorist PD",
                amount: reviewData.policyCoverage.uninsuredPdLimit,
              });
            }
            break;
          case "uninsuredPDDed":
            if (reviewData.policyCoverage.uninsuredPdDeductible) {
              coverageData.push({
                name: "Uninsured PD Deductible",
                amount: reviewData.policyCoverage.uninsuredPdDeductible,
              });
            }
            break;
          case "underinsuredPD":
            if (reviewData.policyCoverage.underinsuredPd) {
              coverageData.push({
                name: "Underinsured Motorist PD",
                amount: reviewData.policyCoverage.underinsuredPd,
              });
            }
            break;
          case "umStackedMedicalPayment":
            if (reviewData.policyCoverage.umStackedMedicalPayment) {
              coverageData.push({
                name: "UM Stacked",
                amount: reviewData.policyCoverage.umStackedMedicalPayment,
              });
            }
            break;
          case "medicalPayment":
            if (reviewData.policyCoverage.medicalPaymentLimit) {
              coverageData.push({
                name: "Medical Payment Limits",
                amount: reviewData.policyCoverage.medicalPaymentLimit,
              });
            }
            break;
          case "accidentalDeath":
            if (reviewData.policyCoverage.accidentalDeathLimit) {
              coverageData.push({
                name: "Accidental Death Limits",
                amount: reviewData.policyCoverage.accidentalDeathLimit,
              });
            }
            break;
        }
      });

      return coverageData;
    };

    const handleEdit = (section: string) => {
      // If we have step navigation capability, navigate to the specific step
      if (onNavigateToStep) {
        const stepMap: { [key: string]: number } = {
          contact: 0, // Contact Information step
          drivers: 1, // Driver Information step
          vehicles: 2, // Vehicle Information step
          coverage: 3, // Policy Coverage step
        };

        const stepIndex = stepMap[section];
        if (stepIndex !== undefined) {
          onNavigateToStep(stepIndex);
          return;
        }
      }

      // Fallback to inline editing if no navigation function available
      setEditingSection(editingSection === section ? null : section);
    };

    // const handleSaveEdit = (section: string, updatedData: any) => {
    //   setReviewData(prev => ({
    //     ...prev,
    //     [section]: { ...prev[section as keyof ReviewData], ...updatedData }
    //   }));
    //   setEditingSection(null);
    // };

    // const handleCancelEdit = () => {
    //   setEditingSection(null);
    // };

    const handleSubmit = () => {
      // Handle policy submission
    };

    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Policy Review</h1>
          <p>Review all policy details before submission</p>
        </div>

        <div className="review-container">
          {/* Contact Information Section */}
          <div className="review-section">
            <div className="review-section-header" id="review-contact-info">
              <div className="review-section-title">
                <h3>Contact Information</h3>
                <span className="review-section-status complete">Complete</span>
              </div>
              <button className="btn btn-secondary" onClick={() => handleEdit("contact")}>
                Edit
              </button>
            </div>
            <div className="review-content">
              <DataTable value={[reviewData.contactInfo]} showGridlines responsiveLayout="scroll" className="custom-datatable">
                <Column
                  field="firstName"
                  header={<span className="datatable-header">First Name</span>}
                  body={(info) => <span className="datatable-bold">{info.firstName}</span>}
                />
                <Column
                  field="lastName"
                  header={<span className="datatable-header">Last Name</span>}
                  body={(info) => <span className="datatable-bold">{info.lastName}</span>}
                />
                <Column
                  field="email"
                  header={<span className="datatable-header">Email</span>}
                  body={(info) => <span className="datatable-cell">{info.email}</span>}
                />
                <Column
                  field="phone"
                  header={<span className="datatable-header">Cell Phone</span>}
                  body={(info) => <span className="datatable-cell">{info.phone}</span>}
                />
                <Column
                  field="dateOfBirth"
                  header={<span className="datatable-header">Date of Birth</span>}
                  body={(info) => <span className="datatable-cell">{new Date(info.dateOfBirth).toLocaleDateString()}</span>}
                />
                <Column
                  field="address"
                  header={<span className="datatable-header">Address</span>}
                  body={(info) => (
                    <span className="datatable-cell">
                      {info.address}, {info.city}, {info.state} {info.zipCode}
                    </span>
                  )}
                />
                <Column
                  field="priorInsurance"
                  header={<span className="datatable-header">Prior Insurance</span>}
                  body={() => (
                    <span className="datatable-cell">{workflowData?.step1?.priorInsurance === "Yes" ? "Yes" : "No"}</span>
                  )}
                />
                <Column
                  field="homeowner"
                  header={<span className="datatable-header">Homeowner</span>}
                  body={() => (
                    <span className="datatable-cell">
                      {workflowData?.step1?.residenceType === "Home" && workflowData?.step1?.residenceStatus === "Own" ? "Yes" : "No"}
                    </span>
                  )}
                />
              </DataTable>
            </div>
          </div>

          {/* Drivers Section */}
          <div className="review-section">
            <div className="review-section-header" id="review-driver-info">
              <div className="review-section-title">
                <h3>Drivers ({reviewData.drivers.length})</h3>
                <span className="review-section-status complete">Complete</span>
              </div>
              <button className="btn btn-secondary" onClick={() => handleEdit("drivers")}>
                {/* <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
              </svg> */}
                Edit
              </button>
            </div>
            <div className="review-content">
              <DataTable value={reviewData.drivers} showGridlines responsiveLayout="scroll" className="custom-datatable">
                <Column
                  field="firstName"
                  header={<span className="datatable-header">Name</span>}
                  body={(driver) => (
                    <span className="datatable-bold">
                      {driver.firstName} {driver.lastName}
                    </span>
                  )}
                />
                <Column
                  field="dateOfBirth"
                  header={<span className="datatable-header">Date of Birth</span>}
                  body={(driver) => <span className="datatable-cell">{new Date(driver.dateOfBirth).toLocaleDateString()}</span>}
                />
                <Column
                  field="gender"
                  header={<span className="datatable-header">Gender</span>}
                  body={(driver) => <span className="datatable-cell">{driver.gender}</span>}
                />
                <Column
                  field="maritalStatus"
                  header={<span className="datatable-header">Marital Status</span>}
                  body={(driver) => <span className="datatable-cell">{driver.maritalStatus}</span>}
                />
                <Column
                  field="relationship"
                  header={<span className="datatable-header">Relationship</span>}
                  body={(driver) => <span className="datatable-cell">{driver.relationship || "-"}</span>}
                />
                <Column
                  field="driverType"
                  header={<span className="datatable-header">Driver Type</span>}
                  body={(driver) => <span className="datatable-cell">{driver.driverType || "-"}</span>}
                />
              </DataTable>
            </div>
          </div>

          {/* Vehicles Section */}
          <div className="review-section">
            <div className="review-section-header" id="review-vehicle-info">
              <div className="review-section-title">
                <h3>Vehicles ({reviewData.vehicles.length})</h3>
                <span className="review-section-status complete">Complete</span>
              </div>
              <button className="btn btn-secondary" onClick={() => handleEdit("vehicles")}>
                {/* <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
              </svg> */}
                Edit
              </button>
            </div>
            <div className="review-content">
              <DataTable value={reviewData.vehicles} showGridlines responsiveLayout="scroll" className="custom-datatable">
                <Column
                  field="vin"
                  header={<span className="datatable-header">VIN</span>}
                  body={(vehicle) => <span className="datatable-bold">{vehicle.vin}</span>}
                />
                <Column
                  field="year"
                  header={<span className="datatable-header">Year</span>}
                  body={(vehicle) => <span className="datatable-cell">{vehicle.year}</span>}
                />
                <Column
                  field="make"
                  header={<span className="datatable-header">Make</span>}
                  body={(vehicle) => <span className="datatable-cell">{vehicle.make}</span>}
                />
                <Column
                  field="model"
                  header={<span className="datatable-header">Model</span>}
                  body={(vehicle) => <span className="datatable-cell">{vehicle.model}</span>}
                />
                <Column
                  field="compDed"
                  header={<span className="datatable-header">Comp Ded</span>}
                  body={(vehicle) => <span className="datatable-cell">{vehicle.compDed || "-"}</span>}
                />
                <Column
                  field="collDed"
                  header={<span className="datatable-header">Coll Ded</span>}
                  body={(vehicle) => <span className="datatable-cell">{vehicle.collDed || "-"}</span>}
                />
              </DataTable>
            </div>
          </div>

          {/* Policy Coverage Section */}
          <div className="review-section">
            <div className="review-section-header" id="review-policy-coverage">
              <div className="review-section-title">
                <h3>Policy Coverage</h3>
                <span className="review-section-status complete">Complete</span>
              </div>
              <button className="btn btn-secondary" onClick={() => handleEdit("coverage")}>
                {/* <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
              </svg> */}
                Edit
              </button>
            </div>
            {/* <div className="review-content">
            <div className="coverage-summary"> */}
            <div className="review-content">
              {/* <h5>Policy Details</h5> */}
              <div
                className="policy-details-row"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: "2rem",
                  alignItems: "center",
                  marginBottom: "1rem",
                  width: "100%",
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <strong>Term</strong>
                  <br />
                  <span>{reviewData.policyCoverage.term ? `${reviewData.policyCoverage.term} Months` : "-"}</span>
                </div>
                <div style={{ textAlign: "center" }}>
                  <strong>Effective Date</strong>
                  <br />
                  <span>
                    {reviewData.policyCoverage.policyEffectiveDate
                      ? new Date(reviewData.policyCoverage.policyEffectiveDate).toLocaleDateString()
                      : "-"}
                  </span>
                </div>
                <div style={{ textAlign: "center" }}>
                  <strong>Expiration Date</strong>
                  <br />
                  <span>
                    {reviewData.policyCoverage.policyExpirationDate
                      ? new Date(reviewData.policyCoverage.policyExpirationDate).toLocaleDateString()
                      : "-"}
                  </span>
                </div>
              </div>
              <DataTable
                value={getCoverageTableData(reviewData, reviewData.contactInfo.state || workflowData?.step1?.state)}
                showGridlines
                responsiveLayout="scroll"
                className="custom-datatable"
              >
                <Column
                  field="name"
                  header={<span className="datatable-header">Coverage</span>}
                  body={(cov) => <span className="datatable-bold">{cov.name}</span>}
                />
                <Column
                  field="amount"
                  header={<span className="datatable-header">Amount</span>}
                  body={(cov) => <span className="datatable-cell">{cov.amount}</span>}
                />
              </DataTable>
            </div>

            {/* </div>
          </div> */}
          </div>
        </div>

        {/* Actions - Show workflow navigation if in stepper, otherwise show review actions */}
        {onNext && onPrevious ? (
          // Workflow navigation
          <div className="form-actions">
            {!isFirstStep && (
              <button className="btn btn-secondary" onClick={onPrevious}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" stroke="currentColor" />
                </svg>
                Previous
              </button>
            )}
            {/* <button type="button" className="btn btn-secondary" onClick={handleClearForm}>
              
              Clear Form
            </button> */}
            {loading ? (
              <button type="submit" className="btn btn-primary" disabled={loading}>
                Saving...
              </button>
            ) : (
              <button className="btn btn-primary" onClick={() => onNext && onNext()}>
                Continue
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3.5" d="M9 5l7 7-7 7" stroke="currentColor" />
                </svg>
              </button>
            )}
          </div>
        ) : (
          // Standalone review actions
          <div className="review-actions">
            <button type="button" className="btn btn-secondary">
              Back to Edit
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleSubmit}>
              Submit for Review
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    );
  };

  // Return Content without Layout if being used in a stepper workflow
  if (onNext || currentStep !== undefined) {
    return <Content />;
  }

  // Return Content wrapped in Layout for standalone page
  return (
    <Layout currentPage="Review">
      <Content />
    </Layout>
  );
}
